import { safeHttpUrl } from '@/src/infrastructure/lib/safeHttpUrl';
import { extractCoupleNames, safeFormatDateLong } from '@/src/infrastructure/lib/weddingDataHelper';
import type { InvitationData } from '../types';

export const PLACEHOLDER_GROOM = 'Groom name';
export const PLACEHOLDER_BRIDE = 'Bride name';
export const PLACEHOLDER_HOST = 'Host name';
export const PLACEHOLDER_VENUE = 'Venue';
export const PLACEHOLDER_DATE = 'Date to be announced';

const PLACEHOLDERS = new Set([PLACEHOLDER_GROOM, PLACEHOLDER_BRIDE, PLACEHOLDER_HOST, PLACEHOLDER_VENUE, PLACEHOLDER_DATE]);

/** True when a resolved value is still the honest placeholder (styled differently in the UI). */
export function isPlaceholder(value: string | undefined): boolean {
  return !value || PLACEHOLDERS.has(value);
}

export interface DioramaCopy {
  groom: string;
  bride: string;
  host: string;
  dateLabel: string;
  timeLabel?: string;
  venue: string;
  message?: string;
  rsvpHref?: string;
  groomPhoto?: string;
  bridePhoto?: string;
  couplePhoto?: string;
  customVideoUrl?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function firstUrl(...values: unknown[]): string | undefined {
  for (const value of values) {
    const url = safeHttpUrl(value);
    if (url) return url;
  }
  return undefined;
}

function personName(person: unknown): string | undefined {
  return text(asRecord(person)?.fullName);
}

const FIXTURE_QUERY = 'kmFixture';

/** Dev-only preview of Rule 9 frames. Production builds pass enabled=false via import.meta.env.DEV. */
export function applyDevPhotoFixture(
  data: InvitationData,
  enabled = import.meta.env.DEV,
  search = typeof window === 'undefined' ? '' : window.location.search,
): InvitationData {
  if (!enabled) return data;
  if (new URLSearchParams(search).get(FIXTURE_QUERY) !== 'photos') return data;
  const custom = asRecord(data.customData) ?? {};
  const expMeta = asRecord(custom.expMeta) ?? {};
  const origin =
    typeof window !== 'undefined' && window.location.origin && window.location.origin !== 'null'
      ? window.location.origin
      : 'http://127.0.0.1:3300';
  return {
    ...data,
    customData: {
      ...custom,
      expMeta: {
        ...expMeta,
        groomPhoto: `${origin}/__km_fixture/groom.png`,
        bridePhoto: `${origin}/__km_fixture/bride.png`,
        couplePhoto: `${origin}/__km_fixture/couple.png`,
        customVideoUrl: `${origin}/__km_fixture/story.mp4`,
      },
    },
  };
}

export function resolveDioramaCopy(data: InvitationData): DioramaCopy {
  const custom = asRecord(data.customData);
  const wedding = asRecord(custom?.wedding);
  const expMeta = asRecord(custom?.expMeta);
  const fromTitle = extractCoupleNames(data.eventName);
  const groom = personName(wedding?.groom) ?? fromTitle?.groom ?? PLACEHOLDER_GROOM;
  const bride = personName(wedding?.bride) ?? fromTitle?.bride ?? PLACEHOLDER_BRIDE;
  const host = text(data.hostName) ?? PLACEHOLDER_HOST;
  const venue = text(data.eventVenue) ?? PLACEHOLDER_VENUE;
  const dateLabel = safeFormatDateLong(data.eventDate, 'en-IN', PLACEHOLDER_DATE);
  const timeLabel = text(data.eventTime);

  return {
    groom,
    bride,
    host,
    dateLabel,
    timeLabel,
    venue,
    message: text(data.customMessage),
    rsvpHref: safeHttpUrl(data.rsvpLink),
    groomPhoto: firstUrl(expMeta?.groomPhoto, asRecord(wedding?.groom)?.photoUrl, asRecord(wedding?.groom)?.photo),
    bridePhoto: firstUrl(expMeta?.bridePhoto, asRecord(wedding?.bride)?.photoUrl, asRecord(wedding?.bride)?.photo),
    couplePhoto: firstUrl(expMeta?.couplePhoto, custom?.couplePhoto),
    customVideoUrl: firstUrl(
      custom?.customVideoUrl,
      expMeta?.customVideoUrl,
      custom?.preWeddingVideoUrl,
    ),
  };
}
